import type { CanalInteraccion } from "@/lib/api";
import type { EstadoImeiUI } from "@/lib/imei";

/**
 * Piezas puras del compositor de la bitácora: recordatorio rápido, etiquetas
 * de canal y estados ofrecidos. Salieron de `TabNotas.tsx` (que ya rozaba el
 * tope de líneas) porque no dependen de nada del componente y así se pueden
 * leer y probar solas.
 */

export const CANAL_LABEL: Record<CanalInteraccion, string> = {
  videollamada: "Videollamada",
  llamada: "Llamada",
  presencial: "Presencial",
  whatsapp: "WhatsApp",
  chat: "Chat",
};

/** Estados ofrecidos al cambiar el estado de un IMEI desde la bitácora (Req 7). */
export const ESTADOS_BITACORA: EstadoImeiUI[] = ["OK", "REV", "CLI", "PROVEEDOR", "CRIT"];

/** Opciones rápidas para "⏰ Recordatorio" junto a la nota (Req 1). */
export type RecCuando = "1h" | "2h" | "4h" | "manana9" | "custom";

export const REC_OPCIONES: { val: RecCuando; label: string }[] = [
  { val: "1h", label: "En 1 hora" },
  { val: "2h", label: "En 2 horas" },
  { val: "4h", label: "En 4 horas" },
  { val: "manana9", label: "Mañana 9:00" },
  { val: "custom", label: "Personalizado" },
];

/**
 * Mínimo del input datetime-local: ahora + 6 min en hora LOCAL (el backend
 * exige >= 5 min futuro). Se construye en hora local del navegador.
 */
export function minDateTimeLocal(): string {
  const d = new Date(Date.now() + 6 * 60 * 1000);
  d.setSeconds(0, 0);
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60 * 1000);
  return local.toISOString().slice(0, 16);
}

/**
 * Arma el sub-objeto `recordatorio` del POST de la nota. 1h/2h/4h van como
 * offset relativo (sin ambigüedad de zona horaria). "Mañana 9:00" y custom se
 * resuelven en el NAVEGADOR (hora de Monterrey del user) y se mandan en UTC
 * con toISOString() — calcularlos en el server daría las 9:00 UTC.
 */
export function recordatorioPayload(
  cuando: RecCuando,
  custom: string,
): Record<string, unknown> | null {
  switch (cuando) {
    case "1h":
      return { offset_cantidad: 1, offset_unidad: "h" };
    case "2h":
      return { offset_cantidad: 2, offset_unidad: "h" };
    case "4h":
      return { offset_cantidad: 4, offset_unidad: "h" };
    case "manana9": {
      const d = new Date();
      d.setDate(d.getDate() + 1);
      d.setHours(9, 0, 0, 0);
      return { remind_at: d.toISOString() };
    }
    case "custom": {
      if (!custom) return null;
      const d = new Date(custom);
      if (Number.isNaN(d.getTime())) return null;
      return { remind_at: d.toISOString() };
    }
  }
}
